import React from 'react'
import { X } from 'lucide-react'

import { TIERS } from '../../lib/stripe'
import { ROLE_NAMES, TREE_LABELS } from '../../lib/universeMap'
import { useCheckout } from '../../hooks/useCheckout'
import './UpgradePanel.css'

// ─────────────────────────────────────────────────────────────────────────────
// UpgradePanel
// mode: 'coming-soon' | 'active' | 'branch'
// ─────────────────────────────────────────────────────────────────────────────

export function UpgradePanel({ isOpen, onClose, mode, tier, treeId }) {
  const { startCheckout, loading: checkoutLoading, error: checkoutError } = useCheckout()

  if (!isOpen) return null

  const tierConfig  = TIERS[tier]
  const treeName    = TREE_LABELS[treeId] || treeId
  const tierName    = { 2: 'Entry Level', 3: 'Mid-Level', 4: 'Senior' }[tier]
  const roleName    = ROLE_NAMES[`t${tier}`]?.[treeId]

  const isComingSoon = mode === 'coming-soon'
  const isBranch     = mode === 'branch'

  // Click handler for the active checkout buttons.
  // Branch unlock = one-time €44.99 for the chosen tree.
  // Tier upgrade buttons are currently disabled (no T2/T3/T4 subscription
  // checkout yet), so this only fires for the branch case.
  async function handleCheckoutClick() {
    if (isBranch && treeId) {
      await startCheckout({ type: 'branch_unlock', treeId })
    }
    // Tier upgrades not wired here — capstone gate controls progression
    // and tier upgrade checkout will be added when T2 subscription is live.
  }

  return (
    <>
      <div
        className={`panel-overlay ${isOpen ? 'panel-overlay--open' : ''}`}
        onClick={onClose}
      />
      <aside className={`upgrade-panel ${isOpen ? 'upgrade-panel--open' : ''}`}>

        <div className="upgrade-panel__header">
          <div className="upgrade-panel__top">
            <span className="upgrade-panel__kicker mono">
              {isComingSoon
                ? `Tier ${tier} — ${tierName}`
                : isBranch
                ? 'Branch Unlock — One-time'
                : `Tier ${tier} — ${tierName}`}
            </span>
            <button className="panel-close-btn" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>
          <div className="upgrade-panel__eyebrow mono">{treeName}</div>
          <div className="upgrade-panel__title">
            {roleName || `T${tier} ${treeName}`}
          </div>
          <div className="upgrade-panel__subtitle">
            {isComingSoon
              ? `T${tier} content is in development. Focus on Foundation first — this is where you're going.`
              : isBranch
              ? `Add the ${treeName} tree. Start at T2 and progress by completing capstones.`
              : `Unlock T${tier} content in your active trees.`}
          </div>
        </div>

        <div className="upgrade-panel__body">
          {isComingSoon ? (
            <ComingSoonBody tier={tier} tierName={tierName} treeName={treeName} />
          ) : isBranch ? (
            <BranchBody treeName={treeName} tierConfig={tierConfig} />
          ) : (
            <TierBody tier={tier} treeName={treeName} tierConfig={tierConfig} />
          )}
        </div>

        <div className="upgrade-panel__footer">
          {isComingSoon ? (
            <button className="btn-upgrade btn-upgrade--muted" disabled>
              Coming soon
            </button>
          ) : isBranch ? (
            <button
              className="btn-upgrade"
              onClick={handleCheckoutClick}
              disabled={checkoutLoading}
            >
              {checkoutLoading
                ? 'Redirecting...'
                : `Unlock ${treeName} — €${(44.99).toFixed(2)}`}
            </button>
          ) : (
            <button className="btn-upgrade btn-upgrade--muted" disabled>
              {`Complete T${tier - 1} capstone to unlock`}
            </button>
          )}
          {checkoutError && (
            <div className="upgrade-error mono" style={{
              marginTop: 8,
              fontSize: '0.7rem',
              color: '#ef4444',
              textAlign: 'center',
            }}>
              {checkoutError}
            </div>
          )}
          <div className="upgrade-disclaimer mono">
            {isComingSoon
              ? 'Pricing will be confirmed at launch.'
              : 'No refunds after access is granted. One-way tier progression. Cancel anytime.'}
          </div>
        </div>
      </aside>
    </>
  )
}

// ── Coming soon body ──────────────────────────────────────────────────────────
function ComingSoonBody({ tier, tierName, treeName }) {
  const roadmap = {
    2: 'T2 content launches after Foundation reaches full capacity. Infrastructure tree ships first.',
    3: 'T3 content follows T2 — mid-level content requires the entry-level foundation to be built first.',
    4: 'T4 senior content is the final build. It will be worth the wait.',
  }

  return (
    <>
      <div className="coming-soon-block">
        <div className="coming-soon-block__icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
        <div className="coming-soon-block__label mono">In development</div>
        <div className="coming-soon-block__desc">
          {roadmap[tier]}
        </div>
      </div>

      <div className="what-title mono" style={{ marginTop: 24 }}>What {tierName} {treeName} covers</div>
      {tier === 2 && (
        <>
          <Feature text={<>Specialist content for the <strong>{treeName}</strong> career path — beyond Foundation generalism</>} />
          <Feature text={<><strong>Human Skills T2</strong> — professional effectiveness, stakeholder management, self-management</>} />
          <Feature text={<><strong>AI Foundations T2</strong> — AI practitioner skills for working IT professionals</>} />
          <Feature text={<>Tree-specific scenarios, real environments, deeper troubleshooting</>} />
        </>
      )}
      {tier === 3 && (
        <>
          <Feature text={<>Mid-level <strong>{treeName}</strong> content — domain ownership, not just contributions</>} />
          <Feature text={<><strong>Human Skills T3</strong> — leadership foundations, navigating organisations</>} />
          <Feature text={<><strong>AI Foundations T3</strong> — AI integration at team and department level</>} />
          <Feature text={<>Complex, multi-system scenarios with real accountability</>} />
        </>
      )}
      {tier === 4 && (
        <>
          <Feature text={<>Senior <strong>{treeName}</strong> content — function leadership, strategic thinking</>} />
          <Feature text={<><strong>Human Skills T4</strong> — senior leadership, performance conversations</>} />
          <Feature text={<><strong>AI Foundations T4</strong> — AI strategy, organisational AI accountability</>} />
          <Feature text={<>Post-mortems that don't flatter. Decisions that matter.</>} />
        </>
      )}
    </>
  )
}

// ── Branch unlock body ────────────────────────────────────────────────────────
function BranchBody({ treeName, tierConfig }) {
  return (
    <>
      <div className="price-block">
        <div className="price-row">
          <span className="price-amt">€44.99</span>
          <span className="price-sub">one-time</span>
        </div>
        <div className="price-note mono">
          No change to your monthly subscription. You are already at T2 — this adds a second tree at the same tier.
        </div>
      </div>
      <div className="what-title mono">What you get</div>
      <Feature text={<>Full T2 <strong>{treeName}</strong> content — accessible immediately after purchase</>} />
      <Feature text={<>Progresses <strong>independently</strong> — T3 unlocks after passing the T2 {treeName} capstone</>} />
      <Feature text={<><strong>Subscription unchanged</strong> — no monthly adjustment</>} />
      <Feature text={<><strong>Permanent unlock</strong> — resubscribing restores access instantly</>} />
    </>
  )
}

// ── Tier upgrade body ─────────────────────────────────────────────────────────
function TierBody({ tier, treeName, tierConfig }) {
  const firstMonth = tierConfig?.firstMonthPrice
    ? `€${(tierConfig.firstMonthPrice / 100).toFixed(2)} first month`
    : null

  return (
    <>
      <div className="price-block">
        <div className="price-row">
          <span className="price-amt">€{(tierConfig?.price / 100).toFixed(2)}</span>
          <span className="price-sub">/ month</span>
        </div>
        {firstMonth && <div className="price-first mono">{firstMonth}</div>}
        <div className="price-note mono">
          Tier upgrade is gated by capstone completion. Once eligible, the upgrade activates automatically.
        </div>
      </div>
      <div className="what-title mono">What T{tier} adds</div>
      <Feature text={<>Deeper <strong>{treeName}</strong> specialisation across T{tier} content</>} />
      <Feature text={<><strong>All unlocked trees upgrade together</strong> — one subscription covers every tree you hold</>} />
      <Feature text={<><strong>Human Skills and AI Foundations</strong> advance to T{tier} automatically</>} />
    </>
  )
}

function Feature({ text }) {
  return (
    <div className="feature">
      <div className="feature__dot" />
      <div className="feature__text">{text}</div>
    </div>
  )
}
